/**
 * Onde fica o cartão do guia (pedido do Marcelo, 28/10/2026). O padrão é o canto de baixo à
 * direita; a pessoa pode arrastar o cartão, e o lugar escolhido fica guardado no navegador.
 *
 * O cartão desvia sozinho do que não pode cobrir:
 * - **essencial:** o que o passo aponta, o balão e os botões amarelos da janela de captura. Nem o
 *   lugar escolhido pela pessoa cobre isso;
 * - **fixo:** botões, campos e controles que não saem com a rolagem (o rodapé da janela de
 *   captura, o zoom do mapa). O lugar padrão desvia deles; o escolhido, não, para o arrastar
 *   continuar valendo.
 * O que rola com a página (os cartões de uma lista) não move o cartão: quem rola tira de baixo
 * dele. Mas conta para a transparência (useLugarDoCartao).
 */
import type { Caixa } from './ancora';

/** Lugar contado a partir do canto mais perto: acompanha a janela quando ela muda de tamanho. */
export interface Lugar {
  h: 'esq' | 'dir';
  /** Distância até a borda `h`. */
  x: number;
  v: 'cima' | 'baixo';
  /** Distância até a borda `v`. */
  y: number;
}

export interface Ponto {
  x: number;
  y: number;
}

export interface Tamanho {
  w: number;
  h: number;
}

export interface Obstaculo extends Caixa {
  essencial: boolean;
  /** Não sai com a rolagem. */
  fixo: boolean;
}

/** Distância das bordas no canto padrão. */
const MARGEM = 16;
/** O mais perto das bordas que o cartão chega. */
const BORDA = 8;
/** Em cima, abaixo da barra do painel (60 px), como o player do LeadTalk. */
const TOPO = 68;
/** De quanto em quanto o cartão procura um lugar livre. */
const PASSO = 16;
/** Um essencial pesa mais que todos os outros juntos. */
const PESO_ESSENCIAL = 1000;

export const LUGAR_PADRAO: Lugar = { h: 'dir', x: MARGEM, v: 'baixo', y: MARGEM };

export function limitar(p: Ponto, cartao: Tamanho, tela: Tamanho): Ponto {
  return {
    x: Math.max(BORDA, Math.min(tela.w - cartao.w - BORDA, p.x)),
    y: Math.max(TOPO, Math.min(tela.h - cartao.h - BORDA, p.y)),
  };
}

export function pontoDoLugar(l: Lugar, cartao: Tamanho, tela: Tamanho): Ponto {
  return limitar(
    {
      x: l.h === 'esq' ? l.x : tela.w - l.x - cartao.w,
      y: l.v === 'cima' ? l.y : tela.h - l.y - cartao.h,
    },
    cartao,
    tela,
  );
}

export function lugarDoPonto(p: Ponto, cartao: Tamanho, tela: Tamanho): Lugar {
  const dir = p.x + cartao.w / 2 > tela.w / 2;
  const baixo = p.y + cartao.h / 2 > tela.h / 2;
  return {
    h: dir ? 'dir' : 'esq',
    x: Math.round(dir ? tela.w - p.x - cartao.w : p.x),
    v: baixo ? 'baixo' : 'cima',
    y: Math.round(baixo ? tela.h - p.y - cartao.h : p.y),
  };
}

export function mesmoLugar(a: Lugar, b: Lugar): boolean {
  return a.h === b.h && a.v === b.v && Math.abs(a.x - b.x) < 1 && Math.abs(a.y - b.y) < 1;
}

export function cruzam(a: Caixa, b: Caixa): boolean {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}

/** Quanto pesa pôr o cartão nesta caixa. O que rola com a página não pesa. */
export function custo(c: Caixa, obstaculos: Obstaculo[]): number {
  let total = 0;
  for (const o of obstaculos) {
    if ((o.essencial || o.fixo) && cruzam(c, o)) total += o.essencial ? PESO_ESSENCIAL : 1;
  }
  return total;
}

interface Escolha {
  /** O lugar da pessoa ou o padrão, já em pixels. */
  base: Ponto;
  /** Onde o cartão está agora. */
  atual: Ponto | null;
  /** A base é o lugar que a pessoa escolheu arrastando. */
  escolhido: boolean;
  cartao: Tamanho;
  tela: Tamanho;
  obstaculos: Obstaculo[];
}

/**
 * Onde pôr o cartão:
 * 1. na base, se ela não cobre nada (o lugar escolhido só precisa não cobrir um essencial);
 * 2. senão, onde ele já está, se ali está livre (o cartão não fica pulando);
 * 3. senão, no primeiro lugar livre subindo pelo mesmo lado, depois descendo, depois do outro lado;
 * 4. sem nenhum livre, na base, se ela não cobre um essencial (fica transparente); senão, no
 *    lugar que cobre menos.
 */
export function escolherPonto({
  base: baseBruta,
  atual,
  escolhido,
  cartao,
  tela,
  obstaculos,
}: Escolha): Ponto {
  const caixa = (p: Ponto): Caixa => ({ x: p.x, y: p.y, w: cartao.w, h: cartao.h });
  const peso = (p: Ponto): number => custo(caixa(p), obstaculos);
  const base = limitar(baseBruta, cartao, tela);
  const pesoBase = peso(base);
  if (pesoBase === 0 || (escolhido && pesoBase < PESO_ESSENCIAL)) return base;
  if (atual) {
    const agora = limitar(atual, cartao, tela);
    if (peso(agora) === 0) return agora;
  }

  const ys: number[] = [];
  const yMin = TOPO;
  const yMax = tela.h - cartao.h - BORDA;
  for (let y = base.y - PASSO; y >= yMin; y -= PASSO) ys.push(y);
  for (let y = base.y + PASSO; y <= yMax; y += PASSO) ys.push(y);
  const outroX = tela.w - base.x - cartao.w;
  const candidatos: Ponto[] = ys.map((y) => ({ x: base.x, y }));
  if (Math.abs(outroX - base.x) >= 1) {
    candidatos.push({ x: outroX, y: base.y }, ...ys.map((y) => ({ x: outroX, y })));
  }

  let melhor = base;
  let menor = pesoBase;
  for (const bruto of candidatos) {
    const p = limitar(bruto, cartao, tela);
    const c = peso(p);
    if (c === 0) return p;
    if (c < menor) {
      melhor = p;
      menor = c;
    }
  }
  return pesoBase < PESO_ESSENCIAL ? base : melhor;
}

const PREFIXO = 'guia_lugar_v1_';

export function lerLugar(userId: string): Lugar | null {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(PREFIXO + userId);
    if (!raw) return null;
    const l = JSON.parse(raw) as Partial<Lugar>;
    const ok =
      (l.h === 'esq' || l.h === 'dir') &&
      (l.v === 'cima' || l.v === 'baixo') &&
      Number.isFinite(l.x) &&
      Number.isFinite(l.y);
    return ok ? (l as Lugar) : null;
  } catch {
    return null;
  }
}

export function guardarLugar(userId: string, lugar: Lugar | null): void {
  if (!userId) return;
  try {
    if (lugar) localStorage.setItem(PREFIXO + userId, JSON.stringify(lugar));
    else localStorage.removeItem(PREFIXO + userId);
  } catch {
    // Sem armazenamento (janela anônima, bloqueio): o lugar vale só até recarregar.
  }
}
